
import { AfterViewInit, ChangeDetectionStrategy, Component, HostBinding, Input, OnInit, ViewChild, inject } from '@angular/core';
import { EmblaCarouselType } from 'embla-carousel';
import { Subject } from 'rxjs';

import { ImageDataDb } from '../../../_shared/data/image-data-db.data';
import { TrackingEvents } from '../../../_shared/data/tracking-events.data';
import { IDynamicElement, ImageData, IMediaItem, IMediaSlider } from '../../../_shared/models';
import { CastPipe } from '../../../_shared/pipes';
import { GtmService } from '../../../_shared/services/gtm.service';
import { SnowplowService } from '../../../_shared/services/snowplow.service';
import { ICustomWindow, WindowRefService } from '../../../_shared/services/window-ref.service';
import { MediaSliderSize } from '../../../_shared/types/media/media-slider-size.type';
import { SliderComponent } from '../../../slider/slider.component';
import { ImageItemComponent } from '../../media-item/image-item/image-item.component';
import { VideoItemComponent } from '../../media-item/video-item/video-item.component';
import { MediaCollectionModalComponent } from '../media-collection-modal/media-collection-modal.component';
import { MediaSliderThumbnailsComponent } from '../media-slider-thumbnails/media-slider-thumbnails.component';

enum ScrollDirection {
    Next,
    Previous,
}

@Component({
    selector: 'sirius-media-slider',
    templateUrl: './media-slider.component.html',
    styleUrl: './media-slider.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    MediaSliderThumbnailsComponent,
    SliderComponent,
    ImageItemComponent,
    VideoItemComponent,
    MediaCollectionModalComponent,
    CastPipe
]
})
export class MediaSliderComponent implements OnInit, IDynamicElement, AfterViewInit {
    private _gtmService = inject(GtmService);
    private _snowplowService = inject(SnowplowService);
    private _windowRef = inject(WindowRefService);

    @Input() type: MediaSliderSize = 'small';
    @Input() items: IMediaItem[] = [];
    @Input() showDescription = false;

    @ViewChild(MediaSliderThumbnailsComponent) thumbnailSlider?: MediaSliderThumbnailsComponent;
    @ViewChild(SliderComponent) bigSlider?: SliderComponent;

    config?: IMediaSlider;
    open = false;
    index = 0;
    imageData: (ImageData | undefined)[] = [];

    private _scroll = new Subject<ScrollDirection>();
    private _select = new Subject<number>();

    private get Window(): ICustomWindow | undefined {
        return this._windowRef.NativeWindow;
    }

    @HostBinding('class.sir-big')
    get isBig() {
        return this.type === 'big';
    }

    get thumbnailCarousel(): EmblaCarouselType | undefined {
        return this.thumbnailSlider?.carousel;
    }

    get bigCarousel(): EmblaCarouselType | undefined {
        return this.bigSlider?.carousel;
    }

    ngOnInit(): void {
        if (this.config) {
            this.type = this.config.type;
            this.items = this.config.items;
            this.showDescription = this.config.showAltText;
        }

        this.addTracking();
    }

    ngAfterViewInit(): void {
        this.imageData = [
            ImageDataDb.media.slider.small.two,
            ImageDataDb.media.slider.small.three,
            ImageDataDb.media.slider.small.fourPlus,
            ImageDataDb.media.slider.big,
        ];
    }

    setIndex(index: number) {
        this.index = index;
    }

    onClose(index: number): void {
        this.thumbnailCarousel?.scrollTo(index, true);

        this.open = false;
    }

    onClick(index: number): void {
        this.open = true;

        this.setIndex(index);
        this._select.next(index);
    }

    onScrollNext() {
        this._scroll.next(ScrollDirection.Next);
    }

    onScrollPrevious() {
        this._scroll.next(ScrollDirection.Previous);
    }

    //#region tracking
    private addTracking() {
        if (!this.Window) {
            return;
        }

        this._select.subscribe((x: number) => {
            const cfg = {
                ...this.items[x].tracking?.parameters,
                index: x,
            };

            this._gtmService.track(TrackingEvents.gtm['carouselSelect']([cfg]));

            const cfg2 = {
                category: 'carousel',
                identifier: this.items[x].tracking?.parameters['identifier'],
                target_url: this.Window?.location.href,
                outbound: false,
                context: [],
            };

            this._snowplowService.track('linkClick', cfg2);
        });

        this._scroll.subscribe((x: ScrollDirection) => {
            const func =
                x === ScrollDirection.Next
                    ? TrackingEvents.gtm['carouselNext']
                    : TrackingEvents.gtm['carouselPrevious'];

            const cfg = {
                ...this.config?.tracking?.parameters,
            };

            this._gtmService.track(func([cfg]));
        });
    }
    //#endregion
}
